import frontendConfig from "./frontendConfig";
import { ExtractDateYmd, IsDateYmd } from "./dateHelper";

const DEFAULT_STATUS = "green";
const EXPIRATION_WARNING_DAYS = Number(frontendConfig.albumWarningDays || 15);
const EXPIRATION_DANGER_DAYS = Number(frontendConfig.albumDangerDays || 5);

const BUSINESS_TIME_ZONE = "America/Cancun";
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Resolves the current business calendar date in Cancun without depending on
 * the browser/device timezone.
 *
 * @param {Date} value Instant to format.
 * @returns {string} Business date in YYYY-MM-DD format.
 */
function GetBusinessDateYmd(value = new Date()) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: BUSINESS_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(value);

    const values = parts.reduce((result, part) => {
        if (["year", "month", "day"].includes(part.type)) {
            result[part.type] = part.value;
        }
        return result;
    }, {});

    return `${values.year}-${values.month}-${values.day}`;
}

/**
 * Converts a YYYY-MM-DD date to an integer day number for timezone-free
 * calendar comparisons.
 *
 * @param {string} value Date in YYYY-MM-DD format.
 * @returns {number|null} UTC day number or null for invalid values.
 */
function GetCalendarDayNumber(value) {
    const date = ExtractDateYmd(value);

    if (!IsDateYmd(date)) {
        return null;
    }

    const [year, month, day] = date.split("-").map(Number);
    return Math.floor(Date.UTC(year, month - 1, day) / DAY_IN_MS);
}

/**
 * Resolves the expiration date for an album card.
 * SUF trusts the ExpiresDate value returned by the photo API and does not
 * calculate expiration dates from reservation dates in the frontend.
 *
 * @param {Object} params Date resolution parameters.
 * @param {Object|null} params.album Parent album object.
 * @param {Object|null} params.park Album park/photo experience object.
 * @returns {string|null} Resolved ExpiresDate or null when not available.
 */
function GetExpirationDate({ album = null, park = null } = {}) {
    return park?.ExpiresDate || album?.ExpiresDate || null;
}

/**
 * Gets the number of business calendar days remaining before the album expires.
 * The comparison uses date-only values and the America/Cancun business calendar,
 * so the browser/device timezone cannot add or remove a visual day.
 *
 * @param {string|null} expiresDate Expiration date to evaluate.
 * @returns {number|null} Remaining days, or null when the date is invalid.
 */
function GetExpirationDayDifference(expiresDate) {
    const targetDay = GetCalendarDayNumber(expiresDate);
    const businessDay = GetCalendarDayNumber(GetBusinessDateYmd());

    if (targetDay === null || businessDay === null) {
        return null;
    }

    return targetDay - businessDay;
}

function GetDaysUntilExpiration(expiresDate) {
    const difference = GetExpirationDayDifference(expiresDate);
    return difference === null ? null : Math.max(difference, 0);
}

/**
 * Resolves the visual status for the expiration badge.
 * Green is the default state; warning and danger thresholds come from MIX_
 * environment variables so each environment can tune the visual alert window.
 *
 * @param {string|null} expiresDate Expiration date to evaluate.
 * @returns {string} Badge status: green, warning, or danger.
 */
function GetExpirationStatus(expiresDate) {
    const days = GetDaysUntilExpiration(expiresDate);

    if (days === null) {
        return DEFAULT_STATUS;
    }

    if (days <= EXPIRATION_DANGER_DAYS) {
        return "danger";
    }

    if (days <= EXPIRATION_WARNING_DAYS) {
        return "warning";
    }

    return DEFAULT_STATUS;
}

/**
 * Formats the translated expiration badge text.
 * The current translations use a pipe-separated format to support singular
 * and plural labels.
 *
 * @param {Object} params Formatting parameters.
 * @param {string|null} params.expiresDate Direct expiration date.
 * @param {Object|null} params.album Parent album object.
 * @param {Object|null} params.park Album park/photo experience object.
 * @param {Function} params.translate Vue i18n translation function.
 * @returns {string} Final text shown in the album badge.
 */
function FormatExpirationText({ expiresDate = null, album = null, park = null, translate }) {
    const resolvedExpiresDate = expiresDate || GetExpirationDate({ album, park });
    const difference = GetExpirationDayDifference(resolvedExpiresDate);
    const count = difference === null ? null : Math.max(difference, 0);
    const safeCount = count === null ? 0 : count;

    if (difference === 0) {
        return translate("gallery.expires_today");
    }

    const text = translate("gallery.expires_in");
    const parts = text.split("|").map((part) => part.trim());

    if (parts.length !== 3) {
        return text.replace("{count}", safeCount);
    }

    const [prefix, pluralText, singularText] = parts;
    const base = prefix.replace("{count}", safeCount);
    const suffix = safeCount === 1 ? singularText : pluralText;

    return `${base} ${suffix}`.trim();
}

/**
 * Checks whether the given park represents an expirable album.
 * Photo and video albums are both expirable when the API returns ExpiresDate.
 * Unlocked state is intentionally ignored because expiration must be visible
 * for purchased and non-purchased albums.
 *
 * @param {Object} album Parent album object.
 * @param {Object} park Album park/photo experience object.
 * @returns {boolean} True when the item can display an expiration badge.
 */
function IsExpirableAlbum(album, park) {
    if (park?.AlbumType !== undefined && park?.AlbumType !== null) {
        return [0, 1].includes(Number(park.AlbumType));
    }

    return [0, 1, 2].includes(Number(album?.Type));
}


/**
 * Determines whether the expiration badge should be rendered.
 * The badge is shown for any expirable album that has an ExpiresDate value.
 *
 * @param {Object} params Validation parameters.
 * @param {Object} params.album Parent album object.
 * @param {Object} params.park Album park/photo experience object.
 * @returns {boolean} True when the badge should be displayed.
 */
function ShouldShowExpirationBadge({ album, park }) {
    return IsExpirableAlbum(album, park) && Boolean(GetExpirationDate({ album, park }));
}


/**
 * Determines whether the album has already passed its visible commercial
 * expiration date. The technical availability window is handled by AWS API
 * through Status; SUF blocks opening and cart actions with ExpiresDate.
 *
 * @param {Object} params Validation parameters.
 * @param {Object|null} params.album Parent album object.
 * @param {Object|null} params.park Album park/photo experience object.
 * @returns {boolean} True when the visible expiration date already passed.
 */
function IsAlbumCommerciallyExpired({ album = null, park = null } = {}) {
    const expiresDate = GetExpirationDate({ album, park });

    if (!expiresDate) {
        return false;
    }

    const targetDay = GetCalendarDayNumber(expiresDate);
    const businessDay = GetCalendarDayNumber(GetBusinessDateYmd());

    if (targetDay === null || businessDay === null) {
        return false;
    }

    return businessDay > targetDay;
}

/**
 * Builds the CSS classes for the expiration badge.
 *
 * @param {string|null} expiresDate Expiration date to evaluate.
 * @returns {string} CSS classes used by Vue.
 */
function GetExpirationBadgeClass(expiresDate) {
    return `album-expiration-badge album-expiration-badge--${GetExpirationStatus(expiresDate)}`;
}

export {
    GetExpirationDate,
    GetDaysUntilExpiration,
    GetExpirationStatus,
    FormatExpirationText,
    ShouldShowExpirationBadge,
    GetExpirationBadgeClass,
    IsExpirableAlbum,
    IsAlbumCommerciallyExpired,
    GetBusinessDateYmd,
};

export default {
    GetExpirationDate,
    GetDaysUntilExpiration,
    GetExpirationStatus,
    FormatExpirationText,
    ShouldShowExpirationBadge,
    GetExpirationBadgeClass,
    IsExpirableAlbum,
    IsAlbumCommerciallyExpired,
    GetBusinessDateYmd,
};
